import {useCallback, useRef, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';

import usePCFStore from '../../../store/pcfStore/pcfStore';
import {syncPcfUrlWithRecordId} from '../utils/pcfRoutes';
import {savePCFRecord, type SavePCFRecordParams} from '../utils/pcfSave';
import {getPCFActionLabel, getPCFSaveDraftLabel} from '../utils/pcfSaveLabels';

interface PCFPersistState {
  isSaving: boolean;
  isUpdating: boolean;
  persist: (params: SavePCFRecordParams) => Promise<void>;
  runWhileSaving: (action: () => Promise<void>) => Promise<void>;
  saveDraftLabel: string;
  actionLabel: (defaultLabel: string) => string;
}

const usePCFPersist = (): PCFPersistState => {
  const navigate = useNavigate();
  const location = useLocation();
  const {id: routeId} = useParams<{id?: string}>();
  const recordId = usePCFStore((state) => state.recordId);
  const storeIsSaving = usePCFStore((state) => state.isSaving);
  const [isPending, setIsPending] = useState(false);
  const isPendingRef = useRef(false);

  const isSaving = storeIsSaving || isPending;
  const isUpdating = Boolean(recordId);

  const persist = useCallback(
    async (params: SavePCFRecordParams) => {
      const hadRecordId = Boolean(usePCFStore.getState().recordId);
      await savePCFRecord(params);
      const savedRecordId = usePCFStore.getState().recordId;

      if (!hadRecordId && savedRecordId) {
        syncPcfUrlWithRecordId(
          navigate,
          location.pathname,
          routeId,
          savedRecordId
        );
      }
    },
    [location.pathname, navigate, routeId]
  );

  const runWhileSaving = useCallback(async (action: () => Promise<void>) => {
    if (isPendingRef.current || usePCFStore.getState().isSaving) {
      return;
    }

    isPendingRef.current = true;
    setIsPending(true);
    try {
      await action();
    } finally {
      isPendingRef.current = false;
      setIsPending(false);
    }
  }, []);

  return {
    isSaving,
    isUpdating,
    persist,
    runWhileSaving,
    saveDraftLabel: getPCFSaveDraftLabel(isSaving, isUpdating),
    actionLabel: (defaultLabel: string) =>
      getPCFActionLabel(defaultLabel, isSaving, isUpdating)
  };
};

export default usePCFPersist;
